<template>
    <div id="portfolio">
        <h1 class="h3 mb-2 mt-4 fw-800 transition-up-1">
            {{ type == 'user' ? $t('companyPage.menu.portfolio') : $t('companyPage.portfolio.h1', {name: profile.name}) }}
        </h1>

        <div class="filters d-flex mb-1 transition-up-2">
            <Search
                v-model="search"
                name="searchcase"
                :placeholder="$t('general.search')"
                class="m-0 flex-grow-1"
                @input="debounceFilter"
                size="10"
            />

            <dd-button
                id="portfolio-sort-toggle"
                name="portfolio-sort"
                class="btn-tertiary ml-2 sort-toggle"
            >
                <div class="icon-ic-sort" />
            </dd-button>
        </div>

        <client-only>
            <dropdown
                anchor-id="portfolio-sort-toggle"
                modal-name="portfolio-sort"
                placement="bottom-end"
            >
                <ul class="dd-list p-1">
                    <li
                        v-for="(sort, i) in sorts"
                        :key="`sort-${i}`"
                        :class="{selected: i == sortIndex}"
                    >
                        <button
                            @click="applySort(i)"
                            menu-item
                            class="menu-item iconed p-1 pr-2"
                        >
                            {{sort.label}}
                            <span
                                v-if="i == sortIndex"
                                class="icon-ic-tick-thin ml-auto dd-header"
                            />
                        </button>
                    </li>
                </ul>
            </dropdown>
        </client-only>

        <transition
            :name="updateTransition"
            mode="out-in"
        >
            <transition-group
                v-if="casesLength || !search"
                :name="loadTransition"
                :key="updatedAt"
                class="row transition-up-3"
            >
                <div
                    v-for="post in cases"
                    :key="`case-${post.id}`"
                    class="col-md-6 mt-2 mb-1"
                >
                    <Case
                        class="h-100"
                        :post="post"
                        @removed="remove(post.id)"
                        @hidden="remove(post.id)"
                    />
                </div>
            </transition-group>

            <div
                class="mt-3 moder-placeholder py-5 px-2 transition-up-3"
                v-else-if="moderPlaceholder"
            >
                <img src="/images/create-placeholder.png" alt="" width="204" height="224">
                <div class="fw-600 mt-2">{{ $t('companyPage.portfolioPlaceholder.title') }}</div>
                <div class="mt-1">
                    {{ $t('companyPage.portfolioPlaceholder.text1') }}<br>
                    {{ $t('companyPage.portfolioPlaceholder.text2') }}
                </div>

                <nuxt-link
                    class="btn-primary mx-auto mt-2 d-inline-block"
                    :to="localePath(createLink)"
                >
                    <span class="icon-ic-union color-gray-3 f-small mr-1"/>{{$t('addMenu.work')}}
                </nuxt-link>
            </div>

            <Card
                v-else
                :lower="true"
                class="mt-3 transition-up-1"
            >
                <p class="color-text">{{ $t('component.ListView.notFoundTitle') }}</p>

                <p class="f-small color-muted my-1">{{ $t('component.ListView.tips') }}</p>
                <ul class="f-small color-muted mt-h list-disk">
                    <li>{{ $t('component.ListView.tip1') }}</li>
                    <li>{{ $t('component.ListView.tip2') }}</li>
                    <li>{{ $t('component.ListView.tip3') }}</li>
                </ul>
            </Card>
        </transition>

        <Pagination
            class="mt-3"
            :page="page"
            :limit="limit"
            :visibleLength="$mq == 'xs' ? 3 : 5"
            :length="casesLength"
            :loading="paginationLoading"
            :busy="paginationBusy"
            @change="goToPage"
            @load="loadPage"
        />
    </div>


</template>

<script>
import Card       from '~/components/cards/Card.vue';
import Case       from '~/components/thumbnails/Case.vue';
import Search     from '~/components/form/SearchInputLow.vue';
import Pagination from '~/components/Pagination.vue';
import DdButton   from '~/components/DdButton.vue';


import debounce    from '~/utilities/debounce.js';

export default {
    name: 'ProfileCases',

    scrollToTop: false,

    components: {
        Card,
        Search,
        Case,
        Pagination,
        Dropdown: () => import('~/components/CasesDropdown.vue'),
        DdButton
    },

    head () {
        return {
            title: this.$t('companyPage.portfolio.title', {
                name: this.profile.displayName || this.profile.name
            }),
        };
    },

    props: ['profile', 'type', 'isModer'],

    data () {
        return {
            sorts: [
                {
                    label: this.$t('companyPage.sortNewFirst'),
                    value: 'pubDate DESC',
                    default: true
                },
                {
                    label: this.$t('companyPage.sortOldFirst'),
                    value: 'pubDate ASC'
                },
                {
                    label: this.$t('companyPage.sortLikes'),
                    value: 'likes DESC'
                },
                {
                    label: this.$t('companyPage.sortPopular'),
                    value: 'views DESC'
                }
            ],

            sortIndex: 0,

            cases: [],
            casesLength: 0,

            limit: 50,
            page: 1,

            search: '',

            paginationBusy: false,
            paginationLoading: false,
            updatedAt: new Date().getTime(),
        }
    },

    async asyncData ({$axios, route, error, app}) {
        let slug = route.params.profile;


        let postData = await $axios.$get(`/publication/find`, {
            params: {
                type: 'case',
                profile: slug,
                limit: 50
            }
        })
        .catch((e) => {
        });

        if (!postData.posts) return error({ statusCode: 404, message: 'Курс не знайдено' });

        return {
            cases: postData.posts,
            casesLength: postData.length
        }
    },

    computed: {
        moderPlaceholder () {
            return  this.isModer && !this.casesLength && !this.search;
        },

        filtered () {
            return !!this.search
        },

        updateTransition () {
            return this.paginationLoading ? 'none' : 'fade-slide-top'
        },

        loadTransition () {
            return this.paginationLoading ? 'none' : 'fade-slide-top'
        },

        query () {
            let obj = {
                limit: this.limit,
                skip : (this.page) * this.limit,
                sort : this.sorts[this.sortIndex].value,
                type : 'case',
                status: 'published'
            }

            obj[this.type] = this.profile.id;

            if (this.search) obj.search = this.search;
            return obj;
        },

        locale () {
            return this.$i18n.locale;
        }
    },

    methods: {
        debounceFilter: debounce(function () {
            this.applyFilter();
        }, 300),

        async get (query) {

            this.paginationBusy = true;

            let postData = await this.$axios.$get(`/publication/find`, {
                params: query
            });

            this.paginationBusy = false;
            return postData;
        },

        async loadPage () {
            this.paginationLoading = true;
            let postData = await this.get(this.query);
            this.paginationLoading = false;

            this.cases.push(...postData.posts);
            this.page++;
        },

        async goToPage (page) {
            let query = Object.assign({}, this.query);
            query.skip = (page-1) * this.limit;
            let postData = await this.get(query);

            let done = () => {
                this.updatedAt = new Date().getTime();
                this.cases = postData.posts;
                this.page = page;
            }

            this.$scrollTo(`#portfolio`, {
                offset: -80,
                onDone: done,
                onCancel: done
            });
        },

        applySort (index) {
            if (this.sortIndex == index) return;

            this.sortIndex = index;
            this.$modalityStore.closeDropdown({name: 'portfolio-sort'});

            this.applyFilter();
        },

        async applyFilter () {
            let query = Object.assign({}, this.query);
            query.skip = 0;
            this.paginationLoading = false;
            let postData = await this.get(query);

            this.updatedAt = new Date().getTime();

            this.page        = 1;
            this.cases       = postData.posts;
            this.casesLength = postData.length;
        },

        remove (id) {
            if (!id) return;
            let index = this.cases.findIndex(o => o.id == id);
            this.cases.splice(index, 1);
        }
    }
}
</script>

<style lang="postcss" scoped>
    .sort-toggle {
        padding: 8px;
        width: 48px;
        height: 48px;
        flex-shrink: 0;
        border-radius: 50%;
        font-size: 17px;
    }

    .dd-carret {
        font-size: 18px;
        transform: rotate(0deg);
        transition: transform .15s ease;
    }

    .active .dd-carret {
        transform: rotate(180deg);
    }

    .moder-placeholder  {
        background-color: var(--color-cases-purple-dim);
        border-radius: 8px;
        text-align: center;
    }
</style>
